import React from 'react';
import clsx from 'clsx';
import { GenericStatusColorType, GenericStatusLabel } from '@hevoio/hevo-ui-toolkit';
import styles from './ListingIdBadge.module.scss';

export interface ListingIdBadgeProps {
  /** The entity's display id, without the leading `#`. */
  id: string | number;
  className?: string;
}

/**
 * The `#ID` cell of a listing table, as a neutral grey badge.
 *
 * Renders the toolkit's `GenericStatusLabel` in its faded-badge mode with the
 * DEFAULT (grey) colour type — so an id reads as the same kind of token as the
 * status chip in the last column, just unemphasised, and the badge styling
 * stays owned by the toolkit instead of a copied class list.
 *
 * No per-instance dataId, deliberately: GenericStatusLabel exposes only its
 * fixed contract id (`generic-status-label`), and every consumer targets the
 * cell through its column's dataId instead — same as the status column.
 */
export function ListingIdBadge({ id, className }: ListingIdBadgeProps) {
  return (
    <GenericStatusLabel
      status={{
        value: id,
        displayName: `#${id}`,
        colorType: GenericStatusColorType.DEFAULT
      }}
      withIcon={false}
      className={className}
    />
  );
}

export interface HeaderIdBadgeProps {
  id: string | number;
  dataId?: string;
  /** `md` is the narrower id column used by list cards. */
  size?: 'md';
  className?: string;
  style?: React.CSSProperties;
  /** Rendered after the badge inside the id column, e.g. a trial chip. */
  children?: React.ReactNode;
}

/** The `#ID` at the start of a detail-page header: the listing badge inside the header's id column. */
export function HeaderIdBadge({ id, dataId, size, className, style, children }: HeaderIdBadgeProps) {
  return (
    <span
      className={clsx(
        'entity-id-container text-nowrap',
        size && `entity-id-container-${size}`,
        styles.headerId,
        className
      )}
      style={style}
      data-id={dataId}
    >
      <ListingIdBadge id={id} />
      {children}
    </span>
  );
}
